// MVP Profile Service
// Handles profile data fetching, updates, image uploads, and account deletion

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type ProfileUpdate = Database['public']['Tables']['profiles']['Update'];

export interface UserProfile extends ProfileRow {
  email?: string; // From auth.users, not in profiles table
}

/**
 * Fetch complete user profile including Entry Quiz data
 */
export async function fetchProfile(userId: string): Promise<UserProfile | null> {
  try {
    // Fetch profile from profiles table
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (profileError) {
      console.error('[ProfileService] Error fetching profile:', profileError);
      return null;
    }

    // Fetch email from auth.users (via user metadata)
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('[ProfileService] Error fetching user:', authError);
    }

    return {
      ...profile,
      email: user?.email,
    };
  } catch (error) {
    console.error('[ProfileService] Unexpected error fetching profile:', error);
    return null;
  }
}

/**
 * Update user profile fields
 */
export async function updateProfile(
  userId: string,
  updates: ProfileUpdate
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId);

    if (error) {
      console.error('[ProfileService] Error updating profile:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    console.error('[ProfileService] Unexpected error updating profile:', error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
}

/**
 * Upload profile image to Supabase Storage
 * Returns the public URL of the uploaded image
 */
export async function uploadProfileImage(
  userId: string,
  file: File
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    // Validate file
    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'File must be an image' };
    }
    if (file.size > 5 * 1024 * 1024) {
      return { success: false, error: 'Image must be less than 5MB' };
    }

    // Determine file extension
    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `profile_image.${extension}`;
    const filePath = `${userId}/${fileName}`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('profile-images')
      .upload(filePath, file, {
        upsert: true, // Replace existing image
        contentType: file.type,
      });

    if (uploadError) {
      console.error('[ProfileService] Error uploading profile image:', uploadError);
      return { success: false, error: uploadError.message };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('profile-images')
      .getPublicUrl(filePath);

    const publicUrl = urlData.publicUrl;

    // Update profile with image URL
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ profile_image_url: publicUrl })
      .eq('user_id', userId);

    if (updateError) {
      console.error('[ProfileService] Error updating profile_image_url:', updateError);
      return { success: false, error: updateError.message };
    }

    return { success: true, url: publicUrl };
  } catch (error: any) {
    console.error('[ProfileService] Unexpected error uploading profile image:', error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
}

/**
 * Upload logo image to Supabase Storage
 * Returns the public URL of the uploaded image
 */
export async function uploadLogoImage(
  userId: string,
  file: File
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    // Validate file
    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'File must be an image' };
    }
    if (file.size > 5 * 1024 * 1024) {
      return { success: false, error: 'Image must be less than 5MB' };
    }

    // Determine file extension
    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `logo_image.${extension}`;
    const filePath = `${userId}/${fileName}`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('profile-images')
      .upload(filePath, file, {
        upsert: true, // Replace existing image
        contentType: file.type,
      });

    if (uploadError) {
      console.error('[ProfileService] Error uploading logo image:', uploadError);
      return { success: false, error: uploadError.message };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('profile-images')
      .getPublicUrl(filePath);

    const publicUrl = urlData.publicUrl;

    // Update profile with logo URL
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ logo_image_url: publicUrl })
      .eq('user_id', userId);

    if (updateError) {
      console.error('[ProfileService] Error updating logo_image_url:', updateError);
      return { success: false, error: updateError.message };
    }

    return { success: true, url: publicUrl };
  } catch (error: any) {
    console.error('[ProfileService] Unexpected error uploading logo image:', error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
}

/**
 * Delete user account
 * Note: Actual account deletion requires server-side handling with service role key
 * For MVP, this function is a placeholder - actual deletion should be handled via Edge Function
 */
export async function deleteAccount(userId: string): Promise<{ success: boolean; error?: string }> {
  // MVP: Account deletion must be handled server-side via Edge Function
  // This prevents exposing service role key to client
  return { 
    success: false, 
    error: 'Account deletion must be handled server-side. Please contact support.' 
  };
}

